import { APP_NAME } from '../constants/app';

const en = {
  common: {
    appName: APP_NAME,
    actions: {
      add: 'Add',
      create: 'Create',
      update: 'Update',
      delete: 'Delete',
      cancel: 'Cancel',
      close: 'Close',
      previous: 'Previous',
      next: 'Next',
      edit: 'Edit',
      moveUp: 'Move up',
      moveDown: 'Move down',
      renameCategory: 'Rename category',
      remove: 'Remove',
      save: 'Save',
      request: 'Request',
      report: 'Report',
      accept: 'Accept',
      decline: 'Decline',
      logout: 'Logout',
      resend: 'Resend',
      openLink: 'Open Link',
    },
    languages: {
      label: 'Language',
      en: 'English',
      fr: 'French',
    },
    statuses: {
      accepted: 'Accepted',
      approved: 'Approved',
      pending: 'Pending',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
      active: 'Active',
      resolved: 'Resolved',
      inProgress: 'In Progress',
      reported: 'Reported',
      purchased: 'Purchased',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
    },
    fields: {
      name: 'Name',
      title: 'Title',
      shortDescription: 'Short Description',
      description: 'Description',
      email: 'Email',
      password: 'Password',
      firstName: 'First Name',
      lastName: 'Last Name',
      address: 'Address',
      city: 'City',
      country: 'Country',
      phone: 'Phone',
      notes: 'Notes',
      message: 'Message',
      location: 'Location',
      status: 'Status',
      category: 'Category',
      severity: 'Severity',
      startDate: 'Start Date',
      endDate: 'End Date',
    },
    labels: {
      supportedFormats: 'Supported formats: {{formats}}',
      currentPhotos: 'Current Photos',
      dragToReorder: 'Drag to reorder. The first photo is the cover.',
      newPhotosToUpload: 'New Photos To Upload',
      uncategorized: 'No category',
      photoCount_one: '{{count}} photo',
      photoCount_other: '{{count}} photos',
      guestCount_one: '{{count}} guest',
      guestCount_other: '{{count}} guests',
      addedOn: 'Added {{date}}',
      invitedOn: 'Invited {{date}}',
      reportedOn: 'Reported {{date}}',
      contribution: 'Contribution',
      contributionAmount: 'Contribution Amount',
      contributionDescription: 'Contribution Description',
      maxGuests: 'Max Guests',
      maxConsecutiveDays: 'Max Consecutive Days',
      maxBookingLeadDays: 'Max Booking Lead Days',
      latitude: 'Latitude',
      longitude: 'Longitude',
      komootUrl: 'Komoot URL',
      estimatedPrice: 'Estimated Price',
      productUrl: 'Product URL',
      houseName: 'House Name',
      numberOfGuests: 'Number of Guests',
      availabilityRules: 'Availability Rules',
      host: 'Host',
      members: 'Members',
      menu: 'Menu',
      guests: 'Guests',
      guide: 'Guide',
      image: 'Image',
      imagePosition: 'Image {{current}} of {{total}}',
      distanceKilometers: '{{value}} km',
    },
    roles: {
      owner: 'Owner',
      host: 'Host',
      guest: 'Guest',
    },
    helpers: {
      optionalCoordinates: 'Optional coordinates for map',
      invalidLatitude: 'Latitude must be between -90 and 90',
      invalidLongitude: 'Longitude must be between -180 and 180',
      contributionUsage: 'Explain how the contribution will be used',
      contributionPerStay: 'Cost per stay (optional)',
      komootOptional: 'Optional link to Komoot route',
      productUrlOptional: 'Optional link to online store',
      selectOrCreateCategory: 'Select an existing category or type a new one',
    },
  },
  layout: {
    myHouses: 'My Houses',
    notifications: 'Notifications',
    accountMenuAria: 'account of current user',
    profile: 'Profile',
  },
  profile: {
    layoutTitle: 'Profile',
    title: 'Profile',
    subtitle: 'Review your account, language, and house activity from one place.',
    quickLinksTitle: 'Quick links',
    accountSecurityTitle: 'Account security',
    preferencesTitle: 'Preferences',
    hostedHomes: 'Host houses',
    visitingHomes: 'Guest houses',
    pendingInvitations: 'Pending invitations',
    languageHelper: 'Change the interface language instantly for your current session.',
    goToHouses: 'Go to houses',
    goToInvitations: 'Go to invitations',
    signedInAs: `Signed in with your current ${APP_NAME} account.`,
    failedFetch: 'Failed to fetch profile data',
    failedSave: 'Failed to save profile changes',
    failedAccountSave: 'Failed to save account settings',
    saveSuccess: 'Profile updated successfully',
    accountSaveSuccess: 'Account settings updated successfully',
    saving: 'Saving...',
    resetProfile: 'Reset changes',
    sessionNote: 'Language changes apply immediately. Profile name changes are saved to your account.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    newPasswordHelper: 'Leave blank if you only want to change your email address.',
    saveAccountSettings: 'Save account settings',
    passwordMismatch: 'New password and confirmation do not match',
  },
  auth: {
    login: {
      title: 'Welcome Back',
      subtitle: 'Sign in to continue to your account',
      submit: 'Sign In',
      submitting: 'Signing in...',
      noAccount: "Don't have an account?",
      signUp: 'Sign up',
      emailRequired: 'Email is required',
      invalidEmail: 'Please enter a valid email address',
      passwordRequired: 'Password is required',
      passwordMin: 'Password must be at least 6 characters',
      failed: 'Failed to login. Please check your credentials.',
      forgotPassword: 'Forgot your password?',
    },
    forgotPassword: {
      title: 'Reset Password',
      subtitle: 'Enter your email and we will send you a reset link.',
      submit: 'Send Reset Link',
      submitting: 'Sending...',
      success: 'If an account matches this email, a reset link has been sent. Check your inbox.',
      failed: 'Something went wrong. Please try again.',
      backToLogin: 'Back to sign in',
    },
    resetPassword: {
      title: 'Set New Password',
      subtitle: 'Choose a new password for your account.',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      submit: 'Reset Password',
      submitting: 'Resetting...',
      success: 'Your password has been updated. You can now sign in.',
      failed: 'This reset link is invalid or has expired.',
      goToLogin: 'Go to sign in',
    },
    register: {
      title: 'Create Account',
      subtitle: `Sign up to get started with ${APP_NAME}`,
      submit: 'Sign Up',
      submitting: 'Creating account...',
      hasAccount: 'Already have an account?',
      signIn: 'Sign in',
      confirmPassword: 'Confirm Password',
      firstNameRequired: 'First name is required',
      firstNameMin: 'First name must be at least 2 characters',
      lastNameRequired: 'Last name is required',
      lastNameMin: 'Last name must be at least 2 characters',
      emailRequired: 'Email is required',
      invalidEmail: 'Please enter a valid email address',
      passwordRequired: 'Password is required',
      passwordMin: 'Password must be at least 6 characters',
      confirmPasswordRequired: 'Please confirm your password',
      passwordsMismatch: 'Passwords do not match',
      failed: 'Failed to create account. Please try again.',
    },
  },
  publicHome: {
    eyebrow: '{{appName}} means hospitality in Greek',
    hero: {
      title: 'Share your home with the people close to you in a thoughtful, serene setting.',
      subtitle:
        'Offer a premium experience around your home: organize availability, prepare each stay with care, and keep the right information perfectly structured.',
      primaryCta: 'Create an account',
      primaryCtaDetail: 'as host or guest',
      secondaryCta: 'Sign in',
      supportingCopy: '{{appName}} helps you welcome trusted people with more clarity, more elegance, and less friction from one stay to the next.',
    },
    features: {
      guide: {
        title: 'A refined house guide',
        description: 'Bring together arrival instructions, house rules, contribution and booking rules, trusted recommendations, and useful contacts in one clear and elegant space.',
      },
      planning: {
        title: 'Smooth availability planning',
        description: 'Coordinate availability and upcoming stays simply, without relying on scattered conversations and last-minute reminders.',
      },
      sharing: {
        title: 'Made for a trusted circle',
        description: 'Share your home with family and friends using the right level of access, in a setup that feels personal and secure.',
      },
    },
    preview: {
      overline: 'What it does',
      title: 'A premium home space designed for trusted sharing.',
      description:
        'Instead of rewriting the same messages for every visit, prepare one elegant, reusable space for family stays, time with friends, or carefully chosen guests.',
      cards: {
        houseGuide: {
          title: 'House guide and trusted recommendations',
          description: 'Keep addresses, instructions, favorite places, and the details that make arrival feel easy and well prepared.',
        },
        sharedPlanning: {
          title: 'Availability and shared stays',
          description: 'Track who is coming, when the home is available, and what activities or preparations matter for a smooth stay.',
        },
      },
      tipLabel: 'Why teams use it',
      tipTitle: 'More trust.',
      tipDescription: 'Your loved ones know where to find the right information, and your home remains easy to share with care and consistency.',
    },
    bottomCta: {
      title: 'Create a trusted home space, then reuse it for every family stay or time with friends.',
      description: '',
      primaryCta: 'Create an account',
      primaryCtaDetail: 'as host or guest',
      secondaryCta: 'I already have an account',
    },
    closeAuthDialog: 'Close authentication dialog',
  },
  dashboard: {
    layoutTitle: `${APP_NAME} - My Houses`,
    title: 'My Houses',
    subtitle: 'Switch between your host and guest houses, then jump into the guide, activities, and planning details.',
    collectionLabel: 'Your collection',
    hostingLabel: 'Host',
    visitingLabel: 'Guest',
    openHouse: 'Open house',
    createNewHouse: 'Create New House',
    noDescription: 'No description available',
    noHouses: 'No houses yet',
    noHousesDescription: 'Create your first house to get started',
    createFirstHouse: 'Create Your First House',
    hosted_one: 'Host ({{count}})',
    hosted_other: 'Host ({{count}})',
    guest_one: 'Guest ({{count}})',
    guest_other: 'Guest ({{count}})',
    failedFetch: 'Failed to fetch houses',
    failedDelete: 'Failed to delete house',
    menuEdit: 'Edit',
    menuDelete: 'Delete',
    deleteTitle: 'Delete House',
    deleteMessage:
      'Are you sure you want to delete this house? This action cannot be undone and will delete all associated data including articles, activities, and bookings.',
  },
  houseDialog: {
    editTitle: 'Edit House',
    createTitle: 'Create New House',
    addPhotos: 'Add Photos',
    addMorePhotos: 'Add More Photos',
    remove: 'Remove',
    failedSave: 'Failed to save house',
    maxOccupants: 'Max Occupants',
    shortDescriptionHelper: 'Shown on the house photo. Keep it short.',
  },
  houseDetail: {
    backToDashboard: 'My houses',
    backToHouse: 'Back to house',
    failedFetch: 'Failed to fetch house details',
    notFound: 'House not found',
    inviteGuest: 'Invite Member',
    layoutTitle: `${APP_NAME} - {{name}}`,
    browseSections: 'Browse the house guide, activities, calendar, and shared planning spaces from one destination page.',
    dashboardLabel: 'House dashboard',
    dashboardTitle: 'Explore house spaces',
    hostedBy: 'Host',
    sharedWith: 'Shared with',
    quickActions: 'Quick actions',
    quickActionsTitle: 'Move faster in this house',
    quickActionsDescription: 'Jump directly to the most common management and planning tasks.',
    categories: {
      guideActivities: {
        title: 'Guide & Activities',
        description: 'House instructions, activities, and local places to keep every stay easy to plan.',
      },
      calendarReservations: {
        title: 'Calendar & Reservations',
        description: 'Availability, booking requests, upcoming stays, and reservation management.',
      },
      other: {
        title: 'Other',
        description: 'Contacts, issues, wishlist items, and member management in one place.',
      },
    },
    actions: {
      openCalendar: 'Open calendar',
      reportProblem: 'Report a problem',
      manageMembers: 'Manage members',
    },
    sections: {
      reservations: 'Reservations',
      reservationsDescription:
        'Review booking requests and upcoming stays from the calendar tools below. This section keeps reservation management grouped with availability.',
      members: 'Members',
      membersDescription:
        'See everyone who has access to this house and manage invitations from the same workspace.',
    },
    reservationSummary: {
      pending: 'Pending requests',
      approved: 'Confirmed stays',
      blocked: 'Blocked periods',
    },
    tabs: {
      guide: 'Guide',
      activities: 'Activities',
      shops: 'Shops',
      restaurants: 'Restaurants',
      calendar: 'Calendar',
      wishlist: 'Wishlist',
      problems: 'Problems',
      contacts: 'Contacts',
    },
  },
  inviteGuest: {
    title: 'Invite Someone to {{houseName}}',
    emailAddress: 'Email Address',
    helper: 'Enter the email address of the person you want to invite',
    accessRole: 'Access Role',
    roleGuest: 'Guest access',
    roleHost: 'Host access',
    guestHelper: 'Guests can view house information and use guest actions like booking requests and suggestions.',
    hostHelper: 'Hosts can manage house content, invitations, bookings, contacts, and other host-level actions.',
    emptyEmail: 'Please enter an email address',
    success: 'Invitation sent successfully!',
    failed: 'Failed to send invitation',
    sending: 'Sending...',
    send: 'Send Invitation',
  },
  guests: {
    empty: 'No members yet. Invite someone to share this house!',
    failedFetch: 'Failed to fetch members',
    failedRemove: 'Failed to remove member',
    removeTitle: 'Remove Member',
    removeMessage:
      'Are you sure you want to remove {{name}} as {{role}} from this house? They will lose the related access.',
    removeInvitationMessage:
      'Are you sure you want to cancel the pending {{role}} invitation for {{name}}?',
    failedResend: 'Failed to resend invitation',
    resent: 'Invitation email sent again',
    removing: 'Removing...',
    removeGuestAria: 'remove member',
    resendInvitationAria: 'resend invitation',
  },
  invitationLanding: {
    layoutTitle: 'Invitation',
    failedFetch: 'Failed to load invitation',
    failedAccept: 'Failed to accept invitation',
    failedReject: 'Failed to reject invitation',
    notFound: 'Invitation not found',
    subtitle: 'You were invited to join {{houseName}} as a {{role}}.',
    location: 'Location',
    invitedEmail: 'Invited email',
    loginRequired: 'Sign in or create an account with this email address to respond to the invitation.',
    wrongAccount: 'This invitation is reserved for {{email}}. Sign in with that account to respond.',
    accepted: 'This invitation has already been accepted.',
    rejected: 'This invitation has already been declined.',
    login: 'Sign In',
    register: 'Create Account',
    back: 'Back',
  },
  invitations: {
    layoutTitle: 'My Invitations',
    title: 'House Invitations',
    subtitle: "You've been invited to join these houses as a host or guest",
    empty: 'No pending invitations',
    emptyDescription: "When someone invites you to join a house, you'll see it here",
    failedFetch: 'Failed to fetch invitations',
    failedAccept: 'Failed to accept invitation',
    failedReject: 'Failed to reject invitation',
    location: 'Location',
    invitedAs: 'Invited as',
  },
  notifications: {
    layoutTitle: 'Notifications',
    title: 'Notifications',
    subtitle: 'Stay up to date with invitations, bookings, problems, and activity suggestions across your houses.',
    failedFetch: 'Failed to fetch notifications',
    empty: 'No notifications',
    emptyDescription: 'When something needs your attention, it will appear here.',
    tabs: {
      all: 'All',
      invitations: 'Invitations',
      bookings: 'Bookings',
      problems: 'Problems',
      activities: 'Activities',
    },
    types: {
      invitation: 'Invitation',
      booking: 'Booking',
      problem: 'Problem',
      activity: 'Activity',
    },
    invitationFrom: '{{name}} invited you to {{house}}',
    invitationForHouse: 'Invitation for {{house}}',
    bookingRequest: '{{name}} requested a stay at {{house}}',
    bookingRequestHouse: 'Booking request for {{house}}',
    bookingStatusChange: 'Your booking at {{house}} was updated',
    problemReported: '"{{title}}" reported at {{house}}',
    problemReportedBy: '{{name}} reported "{{title}}" at {{house}}',
    problemStatusChange: '"{{title}}" at {{house}} was updated',
    activitySuggested: '"{{title}}" suggested at {{house}}',
    activitySuggestedBy: '{{name}} suggested "{{title}}" at {{house}}',
    activityStatusChange: '"{{title}}" at {{house}} was updated',
  },
  articles: {
    layoutTitle: 'House Guide - Articles',
    title: 'House Guide',
    addArticle: 'Add Article',
    noArticles: 'No articles yet',
    noArticlesDescription: 'Create your first guide article to help guests understand your house',
    createFirst: 'Create First Article',
    failedFetch: 'Failed to fetch articles',
    failedDelete: 'Failed to delete article',
    failedSave: 'Failed to save article',
    deleteConfirm: 'Delete this article?',
    editTitle: 'Edit Article',
    createTitle: 'New Article',
    content: 'Content',
  },
  activities: {
    layoutTitle: 'Activities',
    title: 'Activities',
    addActivity: 'Add Activity',
    suggestActivity: 'Suggest Activity',
    noActivities: 'No activities yet',
    ownerEmpty: 'Add activities and attractions around your house',
    guestEmpty: 'Suggest an activity for the owner to review',
    addFirst: 'Add First Activity',
    suggestFirst: 'Suggest First Activity',
    failedFetch: 'Failed to fetch activities',
    failedDelete: 'Failed to delete activity',
    failedApprove: 'Failed to approve activity',
    failedReject: 'Failed to reject activity',
    failedSave: 'Failed to save activity',
    deleteConfirm: 'Delete this activity?',
    viewOnKomoot: 'View on Komoot',
    editTitle: 'Edit Activity',
    createTitle: 'New Activity',
    suggestTitle: 'Suggest Activity',
    readyToUpload: 'Ready to upload',
  },
  shops: {
    layoutTitle: 'Shops',
    title: 'Shops',
    addShop: 'Add Shop',
    noShops: 'No shops yet',
    noShopsDescription: 'List nearby shops and practical places guests may need during their stay',
    createFirst: 'Add First Shop',
    failedFetch: 'Failed to fetch shops',
    failedDelete: 'Failed to delete shop',
    failedSave: 'Failed to save shop',
    deleteConfirm: 'Delete this shop?',
    editTitle: 'Edit Shop',
    createTitle: 'New Shop',
    readyToUpload: 'Ready to upload',
  },
  restaurants: {
    layoutTitle: 'Restaurants',
    title: 'Restaurants',
    addRestaurant: 'Add Restaurant',
    noRestaurants: 'No restaurants yet',
    noRestaurantsDescription: 'Share favorite places to eat around the house for future stays',
    createFirst: 'Add First Restaurant',
    failedFetch: 'Failed to fetch restaurants',
    failedDelete: 'Failed to delete restaurant',
    failedSave: 'Failed to save restaurant',
    deleteConfirm: 'Delete this restaurant?',
    editTitle: 'Edit Restaurant',
    createTitle: 'New Restaurant',
    readyToUpload: 'Ready to upload',
  },
  calendar: {
    layoutTitle: 'Calendar & Bookings',
    title: 'Calendar & Bookings',
    description: 'Check when the house is free, busy, or blocked by the owner.',
    manageRules: 'Manage Rules',
    blockPeriod: 'Block Period',
    requestStay: 'Request Stay',
    noBookings: 'No bookings yet',
    noGuestRequests: 'No guest booking requests yet',
    requestFirstStay: 'Request First Stay',
    requestYourFirstStay: 'Request your first stay for this house',
    failedFetch: 'Failed to fetch bookings',
    failedDelete: 'Failed to delete booking',
    failedApprove: 'Failed to approve booking',
    failedReject: 'Failed to reject booking',
    failedSave: 'Failed to save booking',
    conflictUnavailable: 'This stay conflicts with dates when the house is unavailable.',
    conflictOutsideAvailability: 'This stay is outside the dates currently open for booking.',
    conflictApprovedStay: 'This stay conflicts with an already approved reservation.',
    conflictTooFarAhead: 'This booking is too far in the future.',
    failedSaveRules: 'Failed to save rules',
    failedSaveAvailability: 'Failed to save the unavailable period',
    failedDeleteAvailability: 'Failed to delete the unavailable period',
    deleteConfirm: 'Delete this booking?',
    deleteUnavailableConfirm: 'Delete this unavailable period?',
    bookingRequest: 'Booking request',
    editBooking: 'Edit Booking',
    newBooking: 'New Booking Request',
    request: 'Request',
    rulesTitle: 'Availability Rules',
    maxConsecutiveHelper: 'Maximum number of days for a single booking',
    maxBookingLeadHelper: 'Maximum number of days in advance a booking can be made',
    maxGuestsHelper: 'Maximum number of guests allowed',
    saveRules: 'Save Rules',
    monthOverview: 'Month Overview',
    monthOverviewHelper: 'Select a day to inspect bookings and owner blackout periods.',
    selectionHintStart: 'Click a day to pick your arrival date.',
    selectionHintEnd: 'Now click a day to pick your departure date.',
    today: 'Today',
    legendFree: 'Free',
    legendBusy: 'Busy',
    legendPending: 'Pending',
    legendBlocked: 'Blocked',
    bookingsPanelTitle: 'Bookings and requests',
    ownerBookingsHelper: 'Review guest requests and confirmed stays for the house.',
    guestBookingsHelper: 'Confirmed stays show when the house is occupied. Your own requests remain editable.',
    unavailablePeriodsTitle: 'Owner blackout periods',
    unavailablePeriodsHelper: 'Use blackout periods when the house should not be bookable at all.',
    noUnavailablePeriods: 'No owner blackout periods yet',
    unavailableDialogTitle: 'Add an unavailable period',
    unavailableLabel: 'Label (optional)',
    unavailableLabelHelper: 'Example: maintenance, family stay, seasonal closure',
    saveUnavailablePeriod: 'Save Period',
    blockedLabelFallback: 'Owner unavailable period',
    yourBooking: 'Your booking',
    houseBusy: 'House occupied',
    bookingDetailTitle: 'Bookings for this day',
    hostResponse: 'Host response',
    approveBookingTitle: 'Approve Booking',
    rejectBookingTitle: 'Reject Booking',
    respondMessageLabel: 'Message (optional)',
    respondMessagePlaceholder: 'Add a message for the guest...',
    confirmApprove: 'Approve',
    confirmReject: 'Reject',
  },
  contacts: {
    layoutTitle: 'Contacts',
    title: 'Important Contacts',
    addContact: 'Add Contact',
    noContacts: 'No contacts yet',
    noContactsDescription: 'Add important contacts for emergencies, maintenance, and local services',
    addFirst: 'Add First Contact',
    failedFetch: 'Failed to fetch contacts',
    failedDelete: 'Failed to delete contact',
    failedSave: 'Failed to save contact',
    deleteConfirm: 'Delete this contact?',
    editTitle: 'Edit Contact',
    createTitle: 'New Contact',
    categories: {
      emergency: 'Emergency',
      medical: 'Medical',
      practical: 'Practical',
    },
  },
  wishlist: {
    layoutTitle: 'Wishlist',
    title: 'Wishlist',
    addItem: 'Add Item',
    noItems: 'No wishlist items yet',
    noItemsDescription: 'Add items to your wishlist for guests who want to bring gifts',
    addFirst: 'Add First Item',
    failedFetch: 'Failed to fetch wishlist items',
    failedDelete: 'Failed to delete item',
    failedUpdate: 'Failed to update item',
    failedSave: 'Failed to save item',
    deleteConfirm: 'Delete this item?',
    viewProduct: 'View Product',
    editTitle: 'Edit Item',
    createTitle: 'New Wishlist Item',
  },
  problems: {
    layoutTitle: 'Problems',
    title: 'Known Problems',
    reportProblem: 'Report Problem',
    noProblems: 'No problems reported',
    noProblemsDescription: 'Report any issues or malfunctions to keep guests informed',
    reportFirst: 'Report First Problem',
    failedFetch: 'Failed to fetch problems',
    failedDelete: 'Failed to delete problem',
    failedSave: 'Failed to save problem',
    deleteConfirm: 'Delete this problem?',
    editTitle: 'Edit Problem',
    createTitle: 'Report Problem',
  },
  bedrooms: {
    title: 'Bedrooms',
    addBedroom: 'Add a bedroom',
    addBed: 'Add a bed',
    bedroomName: 'Room name',
    bedroomPlaceholder: 'Bedroom {{number}}',
    bedroomDefault: 'Bedroom {{number}}',
    bedType: 'Type',
    dimensions: 'Size',
    single: 'Single bed',
    double: 'Double bed',
    summaryRooms_one: '{{count}} room',
    summaryRooms_other: '{{count}} rooms',
    summaryBeds_one: '{{count}} bed',
    summaryBeds_other: '{{count}} beds',
    summarySingle_one: '{{count}} single',
    summarySingle_other: '{{count}} singles',
    summaryDouble_one: '{{count}} double',
    summaryDouble_other: '{{count}} doubles',
  },
  amenities: {
    title: 'Amenities',
    categories: {
      kitchen: 'Kitchen',
      laundry: 'Laundry',
      bathroom: 'Bathroom',
      climate: 'Heating & Cooling',
      entertainment: 'Entertainment',
      sports: 'Sports equipment',
      outdoor: 'Outdoor',
      safety: 'Safety',
      connectivity: 'Work',
      parking: 'Parking',
    },
    items: {
      oven: 'Oven',
      microwave: 'Microwave',
      dishwasher: 'Dishwasher',
      fridge: 'Fridge',
      freezer: 'Freezer',
      coffeeMaker: 'Coffee maker',
      kettle: 'Kettle',
      toaster: 'Toaster',
      washingMachine: 'Washing machine',
      dryer: 'Dryer',
      iron: 'Iron',
      bathtub: 'Bathtub',
      shower: 'Shower',
      hairDryer: 'Hair dryer',
      heating: 'Heating',
      airConditioning: 'Air conditioning',
      fireplace: 'Fireplace',
      woodStove: 'Wood stove',
      fan: 'Fan',
      tv: 'TV',
      projector: 'Projector',
      speakers: 'Speakers',
      wifi: 'Wi-Fi',
      boardGames: 'Board games',
      books: 'Books',
      comics: 'Comics',
      billiards: 'Billiards',
      gameConsole: 'Game console',
      paddle: 'Paddleboard',
      canoe: 'Canoe',
      windsurf: 'Windsurf',
      wingfoil: 'Wingfoil',
      garden: 'Garden',
      terrace: 'Terrace',
      bbq: 'Barbecue',
      pool: 'Pool',
      hotTub: 'Hot tub',
      bikesAvailable: 'Bikes available',
      hammock: 'Hammock',
      pingPongTable: 'Ping pong table',
      gardenFurniture: 'Garden furniture',
      smokeDetector: 'Smoke detector',
      firstAidKit: 'First aid kit',
      fireExtinguisher: 'Fire extinguisher',
      workDesk: 'Work desk',
      parking: 'Parking',
      garage: 'Garage',
      evCharger: 'EV charger',
    },
  },
} as const;

export default en;